import { Action, LocalesReducer, LocalesState, initialState } from "./reducer"
import React, { Dispatch, ReactNode, useEffect, useMemo, useReducer } from "react"
import { changeLanguage, changeRegion, toggleOverlay } from "./actions"

import { getCookie } from "../../utils/cookie"
import { regions } from "../../config/regions"

const LocalesStateContext = React.createContext<LocalesState | undefined>(undefined)
const LocalesDispatchContext = React.createContext<Dispatch<Action> | undefined>(undefined)

const useLocalesState = () => {
    const context = React.useContext(LocalesStateContext)
    if (context === undefined) {
        throw new Error("useLocalesState must be used within a LocalesProvider")
    }

    return context
}

const useLocalesDispatch = () => {
    const context = React.useContext(LocalesDispatchContext)
    if (context === undefined) {
        throw new Error("useLocalesDispatch must be used within a LocalesProvider")
    }

    return context
}

type LocalesProviderProps = {
    language: string
    region: string
    children: ReactNode
}

const LocalesProvider = ({ language, region, children }: LocalesProviderProps) => {
    const [state, dispatch] = useReducer(LocalesReducer, {
        ...initialState,
        region: region,
    })

    useEffect(() => {
        const search = new URLSearchParams(window.location.search)
        const region = search.get("region") || getCookie("__region__")

        if (
            !region ||
            !regions
                .filter(r => !process.env.NO_SITE_REGIONS?.includes(r.countryCode))
                .find(r => r.countryCode === region)
        )
            return toggleOverlay(dispatch, { type: "header" })

        changeRegion(dispatch, { region: region })
    }, [])

    const value = useMemo(() => ({ ...state, language }), [state, language])

    useEffect(() => {
        changeLanguage(language)
    }, [language])

    return (
        <LocalesStateContext.Provider value={value}>
            <LocalesDispatchContext.Provider value={dispatch}>{children}</LocalesDispatchContext.Provider>
        </LocalesStateContext.Provider>
    )
}

export { useLocalesState, useLocalesDispatch, LocalesProvider, LocalesStateContext }
